"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CancelTransferRequestSchema = exports.CompleteStageRequestSchema = exports.LinkDeviceRequestSchema = exports.VarSheetRequestSchema = exports.RunNowRequestSchema = exports.ScheduleTransferRequestSchema = exports.UpdateOptionsRequestSchema = exports.LeaseSignRequestSchema = exports.AcceptTransferRequestSchema = exports.SetupIntentRequestSchema = exports.SignatureInputSchema = exports.CreateTransferRequestSchema = exports.confirmPhraseFor = exports.BusinessTransferSchema = exports.TransferTerminalSchema = exports.TransferRunSchema = exports.RunStepResultSchema = exports.StageCompletionSchema = exports.isLinkComplete = exports.MerchantLinkSchema = exports.MerchantLinkKindSchema = exports.DryRunReportSchema = exports.DryRunIssueSchema = exports.LeasedUnitSchema = exports.TransferSignatureSchema = exports.BuyerTargetSchema = exports.TransferPartySchema = exports.CARD_CREDENTIAL_FIELDS = exports.TRANSFER_STATUSES = exports.LEASE_CHOICES = exports.CATALOG_SCOPES = exports.TRANSFER_MODES = void 0;
const zod_1 = require("zod");
/**
 * BUSINESS TRANSFER — a store (carve-out) or a whole business (owner change)
 * moving to a new owner. Spec: docs/superpowers/specs/2026-09-16-store-transfer-design.md
 */
exports.TRANSFER_MODES = ['store', 'business'];
exports.CATALOG_SCOPES = ['priced_at_location', 'whole_catalog'];
exports.LEASE_CHOICES = ['payoff', 'takeover'];
/** Pipeline buckets, in the order the list page strip draws them. */
exports.TRANSFER_STATUSES = [
    'buyer_onboarding', 'card_processing', 'ready_to_run', 'scheduled', 'running',
    'awaiting_merchant_link', 'completed', 'failed', 'cancelled',
];
/** Card credentials stripped on detach and written on link. Never returned by any API. */
exports.CARD_CREDENTIAL_FIELDS = [
    'spinRegisterId', 'spinAuthKey', 'spinTpn',
    'iposApiKeyEncrypted', 'iposSecretKeyEncrypted', 'iposAuthTokenEncrypted',
    'iposApiKeyLast4', 'iposSecretKeyLast4',
    'terminalIdNmi', 'deviceLicense',
];
const Email = zod_1.z.string().trim().toLowerCase().email();
exports.TransferPartySchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    email: Email,
});
exports.BuyerTargetSchema = zod_1.z.object({
    kind: zod_1.z.enum(['new_business', 'existing_business']),
    tenantId: zod_1.z.string().optional(),
    businessName: zod_1.z.string().trim().min(1).optional(),
});
exports.TransferSignatureSchema = zod_1.z.object({
    kind: zod_1.z.enum(['typed', 'drawn']),
    typedName: zod_1.z.string().optional(),
    imageKey: zod_1.z.string().optional(),
    signedAt: zod_1.z.number(),
});
exports.LeasedUnitSchema = zod_1.z.object({
    unitId: zod_1.z.string(),
    label: zod_1.z.string(),
    serialNumber: zod_1.z.string().optional(),
    terminalId: zod_1.z.string().optional(),
    skuId: zod_1.z.string(),
    saleMode: zod_1.z.enum(['installment', 'lease', 'rental']),
    rental: zod_1.z.boolean(),
    monthlyAmountCents: zod_1.z.number().int(),
    principalCents: zod_1.z.number().int(),
    paidCents: zod_1.z.number().int(),
    monthsPaid: zod_1.z.number().int(),
    termMonths: zod_1.z.number().int().optional(),
    remainingCents: zod_1.z.number().int(),
    remainingMonths: zod_1.z.number().int().nullable(),
    stripeSubscriptionId: zod_1.z.string().optional(),
});
exports.DryRunIssueSchema = zod_1.z.object({
    code: zod_1.z.string(),
    message: zod_1.z.string(),
    owner: zod_1.z.enum(['seller', 'buyer', 'rubytill']).default('rubytill'),
});
exports.DryRunReportSchema = zod_1.z.object({
    ranAt: zod_1.z.number(),
    counts: zod_1.z.record(zod_1.z.number()).default({}),
    blockers: zod_1.z.array(exports.DryRunIssueSchema).default([]),
    warnings: zod_1.z.array(exports.DryRunIssueSchema).default([]),
    skipped: zod_1.z.array(zod_1.z.object({ kind: zod_1.z.string(), id: zod_1.z.string(), label: zod_1.z.string(), reason: zod_1.z.string() })).default([]),
    leasedUnits: zod_1.z.array(exports.LeasedUnitSchema).default([]),
    loyaltyPoints: zod_1.z.number().default(0),
});
exports.MerchantLinkKindSchema = zod_1.z.enum(['card_reader', 'online_gateway']);
exports.MerchantLinkSchema = zod_1.z.object({
    /** `emv:<deviceId>` or `gateway:<locationId>` */
    key: zod_1.z.string(),
    kind: exports.MerchantLinkKindSchema,
    label: zod_1.z.string(),
    tenantId: zod_1.z.string(),
    locationId: zod_1.z.string(),
    deviceId: zod_1.z.string().optional(),
    deviceClass: zod_1.z.string().optional(),
    registerId: zod_1.z.string().optional(),
    terminalId: zod_1.z.string().optional(),
    serialNumber: zod_1.z.string().optional(),
    detachedAt: zod_1.z.number().optional(),
    credentialsSavedAt: zod_1.z.number().optional(),
    parametersDownloadedAt: zod_1.z.number().optional(),
    testSaleAt: zod_1.z.number().optional(),
    linkedAt: zod_1.z.number().optional(),
    linkedBy: zod_1.z.string().optional(),
    /** Masked summary for display only, e.g. "TPN …0002". */
    summary: zod_1.z.string().optional(),
});
const isLinkComplete = (l) => !!(l.credentialsSavedAt && l.parametersDownloadedAt && l.testSaleAt);
exports.isLinkComplete = isLinkComplete;
/**
 * NAMED `StageCompletionSchema`/`StageCompletion` HERE ON PURPOSE, matching
 * `journeyRecord.ts`'s shape for the order journey. The two are genuinely
 * different records (this one's `by` carries an email, not a staffId/roleId)
 * so they cannot be merged, and `journeyRecord`'s export cannot be renamed
 * without touching every existing consumer. The package barrel therefore
 * re-exports this pair under `TransferStageCompletionSchema`/
 * `TransferStageCompletion` — see the comment in `index.ts`.
 */
exports.StageCompletionSchema = zod_1.z.object({
    stageId: zod_1.z.string(),
    at: zod_1.z.number(),
    by: zod_1.z.object({ name: zod_1.z.string(), email: zod_1.z.string().optional() }),
    note: zod_1.z.string().optional(),
    undoneAt: zod_1.z.number().optional(),
    undoneBy: zod_1.z.string().optional(),
});
exports.RunStepResultSchema = zod_1.z.object({
    stepId: zod_1.z.string(),
    label: zod_1.z.string(),
    status: zod_1.z.enum(['pending', 'running', 'done', 'failed', 'skipped']),
    attempts: zod_1.z.number().int().default(0),
    startedAt: zod_1.z.number().optional(),
    finishedAt: zod_1.z.number().optional(),
    counts: zod_1.z.record(zod_1.z.number()).optional(),
    output: zod_1.z.record(zod_1.z.any()).optional(),
    error: zod_1.z.string().optional(),
});
exports.TransferRunSchema = zod_1.z.object({
    runId: zod_1.z.string(),
    startedAt: zod_1.z.number(),
    startedBy: zod_1.z.string(),
    finishedAt: zod_1.z.number().optional(),
    status: zod_1.z.enum(['running', 'done', 'failed']),
    steps: zod_1.z.array(exports.RunStepResultSchema),
});
exports.TransferTerminalSchema = zod_1.z.object({
    terminalId: zod_1.z.string(),
    registerName: zod_1.z.string().optional(),
    provisionCode: zod_1.z.string().optional(),
    activatedAt: zod_1.z.number().optional(),
});
exports.BusinessTransferSchema = zod_1.z.object({
    transferId: zod_1.z.string(),
    ref: zod_1.z.string(),
    mode: zod_1.z.enum(exports.TRANSFER_MODES),
    status: zod_1.z.enum(exports.TRANSFER_STATUSES),
    seller: exports.TransferPartySchema,
    requestedBy: zod_1.z.object({ email: zod_1.z.string(), name: zod_1.z.string().optional(), via: zod_1.z.enum(['merchant', 'admin']).default('merchant') }),
    locationId: zod_1.z.string().optional(),
    locationName: zod_1.z.string().optional(),
    buyer: zod_1.z.object({
        name: zod_1.z.string().min(1),
        email: Email,
        tenantId: zod_1.z.string().optional(),
        target: exports.BuyerTargetSchema.optional(),
        stripeCustomerId: zod_1.z.string().optional(),
        acceptedAt: zod_1.z.number().optional(),
        signature: exports.TransferSignatureSchema.optional(),
        leaseSignature: exports.TransferSignatureSchema.optional(),
        paymentMethod: zod_1.z.object({ id: zod_1.z.string(), brand: zod_1.z.string().optional(), last4: zod_1.z.string().optional(), savedAt: zod_1.z.number() }).optional(),
        loginCreated: zod_1.z.boolean().optional(),
        /** Set once the welcome/login-created email for `loginCreated` has been QUEUED — not
         * "delivered", and not even "the send call didn't fail": sendTransferEmail (lib/email.ts)
         * swallows its own SQS/send errors by design, so this timestamp only ever records "we asked
         * to enqueue it". It exists so a step that crashed between AdminCreateUser and the send can
         * retry the send itself, instead of silently skipping it because a login now exists for the
         * email. */
        loginEmailSentAt: zod_1.z.number().optional(),
        /** The Stripe SetupIntent the buyer's card was saved with — billing verifies it before recording the card. */
        setupIntentId: zod_1.z.string().optional(),
    }),
    options: zod_1.z.object({
        catalogScope: zod_1.z.enum(exports.CATALOG_SCOPES).default('priced_at_location'),
        leaseDefault: zod_1.z.enum(exports.LEASE_CHOICES).default('payoff'),
        leaseChoices: zod_1.z.array(zod_1.z.object({ unitId: zod_1.z.string(), choice: zod_1.z.enum(exports.LEASE_CHOICES) })).default([]),
    }),
    acceptToken: zod_1.z.string(),
    dryRun: exports.DryRunReportSchema.optional(),
    varSheet: zod_1.z.object({
        mid: zod_1.z.string(), processor: zod_1.z.string(),
        fileKey: zod_1.z.string().optional(), fileName: zod_1.z.string().optional(),
        uploadedAt: zod_1.z.number(), uploadedBy: zod_1.z.string(),
    }).optional(),
    links: zod_1.z.array(exports.MerchantLinkSchema).default([]),
    terminals: zod_1.z.array(exports.TransferTerminalSchema).default([]),
    completions: zod_1.z.array(exports.StageCompletionSchema).default([]),
    runAt: zod_1.z.number().optional(),
    run: exports.TransferRunSchema.optional(),
    completedAt: zod_1.z.number().optional(),
    cancelledAt: zod_1.z.number().optional(),
    cancelReason: zod_1.z.string().optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
}).superRefine((t, ctx) => {
    if (t.mode === 'store' && !t.locationId) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['locationId'], message: 'a store transfer needs a location' });
    }
});
const confirmPhraseFor = (t) => `TRANSFER ${(t.mode === 'store' ? t.locationName || '' : t.seller.name).trim().toUpperCase()}`;
exports.confirmPhraseFor = confirmPhraseFor;
/* ───────────────────────── API requests ───────────────────────── */
exports.CreateTransferRequestSchema = zod_1.z.object({
    mode: zod_1.z.enum(exports.TRANSFER_MODES),
    locationId: zod_1.z.string().optional(),
    buyerName: zod_1.z.string().trim().min(1),
    buyerEmail: Email,
    catalogScope: zod_1.z.enum(exports.CATALOG_SCOPES).default('priced_at_location'),
    leaseDefault: zod_1.z.enum(exports.LEASE_CHOICES).default('payoff'),
    /** Admin route only: the seller tenant. */
    sellerTenantId: zod_1.z.string().optional(),
}).superRefine((r, ctx) => {
    if (r.mode === 'store' && !r.locationId) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['locationId'], message: 'choose the store being sold' });
    }
});
exports.SignatureInputSchema = zod_1.z.object({
    kind: zod_1.z.enum(['typed', 'drawn']),
    typedName: zod_1.z.string().optional(),
    signatureImagePngBase64: zod_1.z.string().optional(),
});
exports.SetupIntentRequestSchema = zod_1.z.object({ target: exports.BuyerTargetSchema });
exports.AcceptTransferRequestSchema = zod_1.z.object({
    target: exports.BuyerTargetSchema,
    signature: exports.SignatureInputSchema,
    paymentMethodId: zod_1.z.string().min(1),
});
exports.LeaseSignRequestSchema = zod_1.z.object({ signature: exports.SignatureInputSchema });
exports.UpdateOptionsRequestSchema = zod_1.z.object({
    catalogScope: zod_1.z.enum(exports.CATALOG_SCOPES).optional(),
    leaseChoices: zod_1.z.array(zod_1.z.object({ unitId: zod_1.z.string(), choice: zod_1.z.enum(exports.LEASE_CHOICES) })).optional(),
});
exports.ScheduleTransferRequestSchema = zod_1.z.object({ runAt: zod_1.z.number().int(), confirmPhrase: zod_1.z.string() });
exports.RunNowRequestSchema = zod_1.z.object({ confirmPhrase: zod_1.z.string() });
exports.VarSheetRequestSchema = zod_1.z.object({
    mid: zod_1.z.string().trim().min(4),
    processor: zod_1.z.string().trim().min(1),
    fileName: zod_1.z.string().optional(),
});
exports.LinkDeviceRequestSchema = zod_1.z.object({
    credentials: zod_1.z.object({
        spinTpn: zod_1.z.string().regex(/^\d{12}$/, 'TPN must be 12 digits').optional(),
        spinRegisterId: zod_1.z.string().optional(),
        spinAuthKey: zod_1.z.string().optional(),
        iposApiKey: zod_1.z.string().optional(),
        iposSecretKey: zod_1.z.string().optional(),
        iposAuthToken: zod_1.z.string().optional(),
        terminalIdNmi: zod_1.z.string().optional(),
        deviceLicense: zod_1.z.string().optional(),
        securityKey: zod_1.z.string().optional(),
        merchantId: zod_1.z.string().optional(),
    }).optional(),
    parametersDownloaded: zod_1.z.boolean().optional(),
    testSaleApproved: zod_1.z.boolean().optional(),
});
exports.CompleteStageRequestSchema = zod_1.z.object({ note: zod_1.z.string().optional() });
exports.CancelTransferRequestSchema = zod_1.z.object({ reason: zod_1.z.string().trim().min(3) });
//# sourceMappingURL=businessTransfer.js.map